/**
 * 邮件工具函数集合 — 邮件撰写、发送、转发等场景的辅助函数。
 *
 * 主要导出：
 * - `editorContentWithoutSignature` — 获取编辑器内容（去除签名部分）
 * - `wrapSignatureHTML` — 将签名内容包装为带标识的 HTML 容器
 * - `removeAttachmentByIndex` — 按索引从附件列表中移除附件
 * - `fetchRecipientMatches` — 批量匹配收件人邮箱对应的客户
 * - `handleRecipientChipClick` — 收件人标签点击处理（打开客户详情）
 * - `searchRecipientOptions` — 搜索收件人选项（用于自动补全）
 * - `isSentByMe` — 判断邮件是否由当前用户发送
 * - `removeSelfFromRecipients` — 从收件人列表中移除自己的地址
 * - `buildForwardHeaderHtml` — 构造转发邮件的头部 HTML
 * - `buildQuoteCitation` — 构造回复引用头文本（"On ..., ... wrote:"）
 * - `buildQuoteCitationHtml` — 构造经过转义的回复引用头文本
 * - `buildQuoteHeaderHtml` — 构造经过转义的回复引用头 HTML
 * - `buildQuoteBodyHtml` — 获取用于引用块的原邮件正文 HTML（纯文本兜底）
 *
 * 依赖关系：
 * - `customerApi`（客户匹配和收件人搜索）
 * - `Email` 类型
 */
import { customerApi } from "../api.js";
import type { Email } from "../types.js";
import { cleanQuoteHtml } from "./index.js";

/**
 * 将不可信文本转义为可安全拼接到 HTML 文本节点中的内容。
 *
 * @param value - 邮件主题、联系人名称或邮箱等动态文本
 * @returns 完成 HTML 实体转义的字符串
 */
function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * 生成邮件引用头文本（如 "On 2024年1月1日 12:00, 张三 <zhangsan@example.com> wrote:"）。
 * 用于回复邮件时在引用块上方展示原始邮件的日期和发件人信息。
 *
 * @param email - 原始邮件对象，从中提取日期和发件人信息
 * @returns 格式化后的引用头字符串，符合邮件引用惯例
 */
export function buildQuoteCitation(email: Email): string {
  const dateStr = email.email_date
    ? new Date(email.email_date * 1000).toLocaleString("zh-CN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const sender = email.send_from?.name
    ? `${email.send_from.name} <${email.send_from.email || ""}>`
    : email.send_from?.email || "";
  return `On ${dateStr}, ${sender} wrote:`;
}

/**
 * 构造可安全拼接到 HTML 文本节点中的回复引用头文本。
 *
 * @param email - 被回复的原始邮件对象
 * @returns 完成 HTML 实体转义的引用头文本
 */
export function buildQuoteCitationHtml(email: Email): string {
  return escapeHtmlText(buildQuoteCitation(email));
}

/**
 * 构造可直接插入编辑器的回复引用头 HTML。
 * 引用文本中的发件人名称和邮箱会先做实体转义，避免邮件元数据注入 HTML。
 *
 * @param email - 被回复的原始邮件对象
 * @returns 带左边框样式的安全引用头 HTML
 */
export function buildQuoteHeaderHtml(email: Email): string {
  const citation = buildQuoteCitationHtml(email);
  return (
    '<div style="border-left:3px solid #e0e1e6;padding-left:12px;margin:12px 0;color:#60646c;">' +
    `<p>${citation}</p></div>`
  );
}

/**
 * 获取用于引用块的原邮件正文 HTML。
 * 优先使用 html_content；纯文本邮件则将 content 做 HTML 转义并把换行转为 <br>，
 * 保证回复/转发纯文本邮件时引用块不为空。
 *
 * @param email - 原始邮件对象
 * @returns 清理后可直接嵌入引用块的 HTML 字符串
 */
export function buildQuoteBodyHtml(email: Email): string {
  if (email.html_content) return cleanQuoteHtml(email.html_content);
  const escaped = (email.content || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\r?\n/g, "<br>");
}

/**
 * 获取富文本编辑器内容（先移除签名部分再返回）。
 * @param editor - 富文本编辑器实例（需实现 stripSignature 方法和 value getter）
 * @returns 去除签名后的 HTML 内容，editor 为空时返回空字符串
 */
export function editorContentWithoutSignature(editor: any): string {
  if (!editor) return "";
  editor.stripSignature?.();
  return editor?.value || "";
}

/**
 * 将签名 HTML 包装在带 data-signature-id 属性的 div 容器中。
 * 这样编辑器可以识别签名区域并在发送时选择性移除。
 * @param sigId - 签名 ID
 * @param sigContent - 签名 HTML 内容
 * @returns 包装后的 HTML 字符串
 */
export function wrapSignatureHTML(sigId: number, sigContent: string): string {
  return `<div data-signature-id="${sigId}">${sigContent}</div>`;
}

/**
 * 按索引从附件列表中移除指定附件（返回新数组，不修改原数组）。
 * @param files - 附件数组
 * @param index - 要移除的索引
 * @returns 移除后的新数组
 */
export function removeAttachmentByIndex<T>(files: T[], index: number): T[] {
  return files.filter((_, i) => i !== index);
}

/**
 * 批量匹配收件人邮箱对应的客户。
 * 合并 to 和 cc 收件人列表后去重，调用 API 批量匹配。
 * @param toRecipients - 收件人邮箱列表
 * @param ccRecipients - 抄送人邮箱列表
 * @returns Map<邮箱(小写), 客户对象>，无匹配时返回空 Map
 */
export async function fetchRecipientMatches(
  toRecipients: string[],
  ccRecipients: string[],
): Promise<Map<string, any>> {
  const allEmails = [
    ...new Set([...toRecipients, ...ccRecipients]),
  ].filter(Boolean);
  if (allEmails.length === 0) return new Map();
  try {
    const results = await customerApi.matchByEmails(allEmails);
    return new Map(
      results.map((m: any) => [m.email.toLowerCase(), m.customer]),
    );
  } catch {
    return new Map();
  }
}

/**
 * 收件人标签点击处理 — 如果该邮箱匹配到客户，则打开客户详情弹窗。
 * @param email - 被点击的收件人邮箱
 * @param matchedMap - 邮箱到客户的匹配 Map
 * @param shadowRoot - 组件的 ShadowRoot（用于查找客户详情弹窗元素）
 */
export function handleRecipientChipClick(
  email: string,
  matchedMap: Map<string, any>,
  shadowRoot: ShadowRoot | null,
) {
  const customer = matchedMap.get(email.toLowerCase());
  if (customer) {
    const modal = shadowRoot?.querySelector("#customerViewModal") as any;
    if (modal) modal.show(customer);
  }
}

/**
 * 搜索收件人选项 — 调用 API 搜索客户，返回 label/value 格式的选项列表。
 * 用于收件人输入框的自动补全。
 * @param query - 搜索关键词
 * @returns 选项数组，label 格式为 "姓名/公司 <邮箱>"，value 为邮箱
 */
export async function searchRecipientOptions(
  query: string,
): Promise<Array<{ label: string; value: string }>> {
  const data = await customerApi.searchRecipients(query);
  return (data || []).map((c: any) => ({
    label: `${c.name || c.company_name} <${c.email}>`,
    value: c.email,
  }));
}

/** 判断邮件是否由当前用户发出（基于 folder 字段）。folder 为 sent 或 outbox 时返回 true。 */
export function isSentByMe(email: Email | null | undefined): boolean {
  if (!email) return false;
  const f = email.folder;
  return f === "sent" || f === "outbox";
}

/** 从收件人列表中剔除自己的地址（大小写不敏感 + trim）。 */
export function removeSelfFromRecipients(
  addresses: string[],
  selfEmails: string[],
): string[] {
  const selfSet = new Set(
    selfEmails.map((a) => a.toLowerCase().trim()).filter(Boolean),
  );
  return addresses.filter((a) => !selfSet.has(a.toLowerCase().trim()));
}

/**
 * 构造转发头 HTML（与 emails.ts 现有格式一致）。
 * 包含日期、发件人、收件人、抄送、主题等信息，以带左边框的引用块呈现。
 * @param email - 被转发的邮件对象
 * @returns 转发头 HTML 字符串，email 为 null/undefined 时返回空字符串
 */
export function buildForwardHeaderHtml(email: Email | null | undefined): string {
  if (!email) return "";
  const date = new Date((email.email_date || 0) * 1000);
  const dateStr = date.toLocaleString("zh-CN");
  const from = escapeHtmlText(email.send_from?.email || "");
  const fromName = escapeHtmlText(email.send_from?.name || email.send_from?.email || "");
  const to = (email.send_to || [])
    .map((r: any) => r?.email || "")
    .filter(Boolean)
    .map(escapeHtmlText)
    .join(", ");
  const cc = (email.cc || [])
    .map((r: any) => r?.email || "")
    .filter(Boolean)
    .map(escapeHtmlText)
    .join(", ");
  const subject = escapeHtmlText(email.subject || "");

  let header = `<div style="border-left:3px solid #ccc;padding-left:12px;margin:12px 0;color:#555;">`;
  header += `<p><b>---------- 转发的邮件 ----------</b></p>`;
  header += `<p>日期: ${dateStr}</p>`;
  header += `<p>发件人: ${fromName}${fromName !== from ? ` &lt;${from}&gt;` : ""}</p>`;
  header += `<p>收件人: ${to}</p>`;
  if (cc) header += `<p>抄送: ${cc}</p>`;
  header += `<p>主题: ${subject}</p></div>`;
  return header;
}
